import { TypeValue } from '@ha/algolia';
import { UnitType } from '@ha/api-v2/types/inventory/UnitType';
import type { ListingCardPriceInformation } from '@ha/core/Listing/getListingCardPriceInformation';
import { getListingCardPriceInformation } from '@ha/core/Listing/getListingCardPriceInformation';
import { LocalDate } from '@ha/utilities/Dates/LocalDate';

import { ListingCardModel } from 'ha/models/Listing/types';

import { getListingCardRentReduction } from 'ha/components/Redesign/ListingCard/utilities/getListingCardRentReduction';
import { toListingCardPriceListing } from 'ha/components/Redesign/ListingCard/utilities/toListingCardPriceListing';

import { getRentReductionDisplay } from './getRentReductionDisplay';

export type ListingRentReductionAnalytics = {
  has_rent_reduction: boolean;
  rent_reduction_pct?: number;
};

/**
 * Analytics props for rent-reduction UI parity. Pass the same `priceInformation`
 * the card used so `has_rent_reduction` cannot be true without a visible highlight.
 */
export function getListingRentReductionAnalytics({
  listing,
  priceInformation,
}: {
  listing: ListingCardModel;
  priceInformation: ListingCardPriceInformation | null;
}): ListingRentReductionAnalytics {
  const reduction = getListingCardRentReduction({
    listing,
    priceInformation,
  });

  if (!reduction || !priceInformation || priceInformation.type !== 'value') {
    return { has_rent_reduction: false };
  }

  const relativeDrop =
    (reduction.previousPrice - priceInformation.value) /
    reduction.previousPrice;

  return {
    has_rent_reduction: true,
    rent_reduction_pct: Math.round(relativeDrop * 100),
  };
}

/**
 * Builds price info the same way listing cards do (`startDate ?? today`), then
 * returns rent-reduction analytics props.
 */
export function getListingRentReductionAnalyticsFromCard({
  listing,
  startDate,
  endDate,
  categories,
}: {
  listing: ListingCardModel;
  startDate: LocalDate | null;
  endDate: LocalDate | null;
  categories?: TypeValue[];
}): ListingRentReductionAnalytics {
  const priceInformation = getListingCardPriceInformation({
    listing: toListingCardPriceListing(listing),
    dateRange: {
      from: (startDate ?? LocalDate.today()).toDate(),
      to: endDate?.toDate(),
    },
    categories,
  });

  return getListingRentReductionAnalytics({
    listing,
    priceInformation,
  });
}

type ListingUnitTypeRentReduction = Pick<
  UnitType,
  'pricingType' | 'price' | 'oldPrice' | 'rentReducedUntil'
> & {
  hasLengthOfStayDiscount?: boolean;
};

/**
 * Analytics props for LDP rent-reduction UI parity. Uses the same eligibility
 * gate as the listing detail price surfaces (`getRentReductionDisplay`).
 */
export function getListingRentReductionAnalyticsFromUnitType({
  pricingType,
  price,
  oldPrice,
  rentReducedUntil,
  hasLengthOfStayDiscount,
}: ListingUnitTypeRentReduction): ListingRentReductionAnalytics {
  const reduction = getRentReductionDisplay({
    pricingType,
    currentPriceCents: price,
    oldPriceCents: oldPrice,
    rentReducedUntil,
    hasLengthOfStayDiscount,
  });

  if (!reduction) {
    return { has_rent_reduction: false };
  }

  const relativeDrop =
    (reduction.oldPriceCents - price) / reduction.oldPriceCents;

  return {
    has_rent_reduction: true,
    rent_reduction_pct: Math.round(relativeDrop * 100),
  };
}
